// Sequencer Connect - typed browser client for /api/sequencer-runs.
//
// Every shape below is derived from the router (src/routes/sequencer-runs.ts)
// and the repo layer (src/services/database/repos/sequencer-connect-runs.ts).
// The router is the contract; the design doc is not.
//
// SECURITY: a run borrows two secrets from the customer - the sequencer API
// key and one mailbox password per account (PlusVibe's partner API takes the
// same key, so no partner login is borrowed any more). None of them can reach
// this file: RUN_SELECT / ITEM_SELECT in the repo carry only `has_api_key` /
// `has_item_passwords` booleans plus the `api_key_verified_at` /
// `secrets_purged_at` TIMESTAMPS, which are metadata about a secret and never a
// secret. No type in this file
// has a field that holds a secret, and none should ever be given one - the API
// key travels one way only, in setApiKey's request body.
//
// Timestamps arrive as ISO strings.

import { ApiError, api } from '../api/client';

export type { ApiError } from '../api/client';

const BASE = '/api/sequencer-runs';

/* ─── Types ─────────────────────────────────────────────────────────────── */

/** Run lifecycle. `archived_at` is ORTHOGONAL to this - a run can be archived in
 *  any status, so never treat "archived" as a status value. */
export type SqRunStatus = 'draft' | 'in_progress' | 'running' | 'qa' | 'completed' | 'failed';

/** Which platform a run connects to. The params shape and several wizard steps
 *  follow it; the run row is the discriminant, never a field inside params. */
export type SqSequencer = 'plusvibe' | 'instantly' | 'smartlead' | 'bison' | 'warmuptool';

/**
 * Per-mailbox outcome.
 *
 * `connected` is NOT terminal: OAuth consent landed and the account exists in the
 * workspace, but the second push (sending config + tags) has not. Only `done`,
 * `already` and `error` are terminal.
 */
export type SqItemStatus = 'pending' | 'running' | 'connected' | 'done' | 'already' | 'error';

/** One workspace the run's API key can see. `workspaces_cache` is the allow-list
 *  the server validates the workspace choice against, not a hint. */
export interface SqWorkspace {
  id: string;
  name: string;
}

/**
 * Sending/warmup profile pushed to PlusVibe. Mirrors PlusVibeParams.
 * `camp_ramup_start` is PlusVibe's own key name (their typo), kept verbatim so
 * the payload stays copy-paste comparable with theirs.
 */
export interface SqPlusVibeParams {
  min_interval: number;
  daily_limit: number;
  enable_camp_rampup: 'yes' | 'no';
  camp_ramup_start: number;
  camp_rampup_increment: number;
  enable_warmup: 'yes' | 'no';
  warmup_daily_limit: number;
  enable_warmup_rampup: 'yes' | 'no';
  warmup_rampup_start: number;
  warmup_rampup_increment: number;
  reply_rate_pct: number;
  /**
   * Tags beyond the standardized `tag_name`. `resolved_ids` is written by Start
   * ONLY, from a live tag listing, and the params route rejects it on input - a
   * client must never send tag ids nobody verified exist.
   */
  extra_tags: { existing_ids: string[]; new_names: string[]; resolved_ids?: string[] };
}

/**
 * Sending/warmup profile pushed to Instantly. Mirrors InstantlyParams:
 * - `reply_rate_pct` is a PERCENT on write AND read on this platform.
 * - `open_rate` / `spam_save_rate` / `important_rate` are the three
 *   warmup.advanced sliders (0-100).
 * - the three booleans map onto warmup.advanced.{weekday_only, read_emulation,
 *   warm_ctd}.
 * - `include_tracking` preselected true: the pipeline creates inst.<domain>
 *   CNAMEs in our Cloudflare and attaches the tracking domain per account.
 */
export interface SqInstantlyParams {
  daily_limit: number;
  sending_gap: number;
  /** Instantly's gradual send-volume ramp - a top-level account setting, not a
   *  warmup one. */
  enable_slow_ramp: boolean;
  enable_warmup: 'yes' | 'no';
  warmup_limit: number;
  warmup_increment: number;
  reply_rate_pct: number;
  /** Instantly's warmup pool tag. Empty means the run does not use one. */
  warmup_custom_ftag: string;
  open_rate: number;
  spam_save_rate: number;
  important_rate: number;
  weekday_only: boolean;
  read_emulation: boolean;
  warm_ctd: boolean;
  include_tracking: boolean;
  /** `group_tag_ids` is written by programmatic (px3) runs only - the wizard
   *  paste format has no per-account group column. */
  extra_tags: {
    existing_ids: string[];
    new_names: string[];
    resolved_ids?: string[];
    group_tag_ids?: Record<string, string>;
  };
}

/**
 * Sending/warmup profile pushed to Smartlead. Mirrors the Smartlead branch of
 * SequencerParams. Flatter than the PlusVibe shape: Smartlead expresses the same
 * knobs with its own field names, and warmup rampup is a single daily step
 * rather than a start/increment pair.
 */
export interface SqSmartleadParams {
  max_email_per_day: number;
  time_to_wait_in_mins: number;
  enable_warmup: 'yes' | 'no';
  total_warmup_per_day: number;
  enable_warmup_rampup: 'yes' | 'no';
  daily_rampup: number;
  reply_rate_pct: number;
  tracking: { enabled: boolean; prefix: string };
  /**
   * Tags beyond the standardized `tag_name`. `resolved_ids` is written by Start
   * ONLY, from a live tag listing, and the params route rejects it on input - a
   * client must never send tag ids nobody verified exist.
   */
  extra_tags: { existing_ids: string[]; new_names: string[]; resolved_ids?: string[] };
}

/**
 * Sending/warmup profile pushed to Bison. Mirrors BisonParams: the subset of
 * the profile that platform supports - no sending gap, no ramp-up numbers and
 * no reply rate, because Bison ramps warmup itself. The Pick<> keeps the key
 * names shared with PlusVibe rather than inventing a third spelling.
 */
export type SqBisonParams = Pick<
  SqPlusVibeParams,
  'daily_limit' | 'enable_warmup' | 'warmup_daily_limit' | 'extra_tags'
>;

/** The sending profile for whichever sequencer this run targets. `params` on a
 *  run is one of these, discriminated by `SqRun.sequencer`. */
export type SqParams = SqPlusVibeParams | SqInstantlyParams | SqSmartleadParams | SqBisonParams;

/** The canonical PlusVibe sending profile, prefilled into the parameters step.
 *  Same values as CANONICAL_PARAMS in the repo; keep the two in step. */
export const CANONICAL_SQ_PARAMS: SqPlusVibeParams = {
  min_interval: 5,
  daily_limit: 15,
  enable_camp_rampup: 'no',
  camp_ramup_start: 3,
  camp_rampup_increment: 1,
  enable_warmup: 'yes',
  warmup_daily_limit: 12,
  enable_warmup_rampup: 'yes',
  warmup_rampup_start: 3,
  warmup_rampup_increment: 3,
  reply_rate_pct: 42,
  extra_tags: { existing_ids: [], new_names: [] },
};

/** Canonical Instantly profile (2026-08-12 proven run). Same keep-in-step rule
 *  with CANONICAL_INSTANTLY_PARAMS in the repo. */
export const CANONICAL_SQ_INSTANTLY_PARAMS: SqInstantlyParams = {
  daily_limit: 15,
  sending_gap: 5,
  enable_slow_ramp: false,
  enable_warmup: 'yes',
  warmup_limit: 12,
  warmup_increment: 3,
  reply_rate_pct: 42,
  warmup_custom_ftag: '',
  open_rate: 70,
  spam_save_rate: 70,
  important_rate: 70,
  weekday_only: false,
  read_emulation: false,
  warm_ctd: false,
  // Preselected: tracking is INCLUDED by default (operator decision 2026-08-12);
  // the wizard offers the opt-out.
  include_tracking: true,
  extra_tags: { existing_ids: [], new_names: [] },
};

/** The canonical Smartlead sending profile, prefilled into the parameters step.
 *  Same values as CANONICAL_SMARTLEAD_PARAMS in the repo; keep the two in step. */
export const CANONICAL_SQ_SMARTLEAD_PARAMS: SqSmartleadParams = {
  max_email_per_day: 15,
  time_to_wait_in_mins: 5,
  enable_warmup: 'yes',
  total_warmup_per_day: 12,
  // Operator standard 2026-08-25: rampup ON at the floor of 5. Smartlead
  // refuses anything under 5, so our Instantly increment of 3 does not port.
  enable_warmup_rampup: 'yes',
  daily_rampup: 5,
  reply_rate_pct: 42,
  tracking: { enabled: true, prefix: 'trk' },
  extra_tags: { existing_ids: [], new_names: [] },
};

/** Canonical Bison profile. Same keep-in-step rule with BISON_CANONICAL_PARAMS
 *  in the repo. */
export const CANONICAL_SQ_BISON_PARAMS: SqBisonParams = {
  daily_limit: 15,
  enable_warmup: 'yes',
  warmup_daily_limit: 12,
  extra_tags: { existing_ids: [], new_names: [] },
};

export interface SqRun {
  id: string;
  /** The full Google Sheet name. A label only - nothing reads Sheets. */
  name: string;
  sequencer: SqSequencer;
  /**
   * The Bison instance origin this run talks to, null on every platform with a
   * central API host. Not a secret: it is stored in the clear, comes back on
   * every read and is displayed on the summary - but it is half the credential
   * on Bison and Warmuptool, so it is captured and verified together with the
   * API key.
   */
  base_url: string | null;
  created_by: string;
  created_by_role: string;
  status: SqRunStatus;
  /** Wizard pointer, 1..6. Server-held, so a run resumes on any machine. */
  current_step: number;
  /** True iff the server currently holds a verified API key for this run. */
  has_api_key: boolean;
  api_key_verified_at: string | null;
  /** Legacy: the pipl login older PlusVibe runs borrowed from the sheet. Null
   *  on every run since the partner API moved to key auth. */
  pv_username: string | null;
  /** Legacy: whether a partner password is held. False on every run since the
   *  partner API moved to key auth; use has_item_passwords. */
  has_pv_password: boolean;
  /** True iff ANY item still holds its mailbox password - the "passwords
   *  survived a purge" signal on every platform. */
  has_item_passwords: boolean;
  secrets_purged_at: string | null;
  workspaces_cache: SqWorkspace[] | null;
  workspace_id: string | null;
  workspace_name: string | null;
  /** Smartlead only: the workspace user id the accounts are created under, either
   *  auto-resolved from the API key or entered by the operator. A plain
   *  identifier, never a secret - null on a PlusVibe run. */
  sl_user_id: string | null;
  /** Always null on a connect-only warmuptool run. */
  params: SqParams | null;
  /** Standardized tag `PI_YYYY_MM_DD`, stamped at creation. */
  tag_name: string;
  tag_id: string | null;
  provider_id: string | null;
  accounts_total: number;
  accounts_done: number;
  accounts_already: number;
  accounts_errored: number;
  /** Run-level catastrophe only (the enqueue itself failed), never a per-item
   *  failure - those live on the item rows. */
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Set by Archive, in ANY status, and it always purges the secrets. Orthogonal
   *  to `status`: read it before painting a badge. */
  archived_at: string | null;
}

export interface SqRunItem {
  run_id: string;
  /** Zero-based, from the paste order. The list is ordered by it. */
  position: number;
  email: string;
  status: SqItemStatus;
  /** PlusVibe's id for the connected account, kept once consent lands. */
  account_id: string | null;
  error_code: string | null;
  error_message: string | null;
  connected_at: string | null;
  configured_at: string | null;
  qa_verified_at: string | null;
  /** Bounded by the per-item cap. Charged by the QA auto-retry only - a manual
   *  retry resets it to 0, because the operator fixed a root cause. */
  qa_attempts: number;
  started_at: string | null;
  finished_at: string | null;
}

/** GET /:id */
export interface SqRunDetail {
  run: SqRun;
  items: SqRunItem[];
}

/** POST /:id/api-key */
export interface SqSetApiKeyResult {
  verified: true;
  /** Smartlead returns an owner ID instead of a workspace list. */
  workspaces: SqWorkspace[];
  sl_user_id?: string | null;
}

/** POST /:id/accounts. `duplicates` are rows dropped as repeats of an earlier
 *  line (keep-first); they are NOT an error, but the team has to see them. */
export interface SqSetAccountsResult {
  count: number;
  duplicates: { line: number; email: string }[];
}

/** One bad row from the accounts paste. `line` is 1-based against the ORIGINAL
 *  paste, so it points at the row in the textarea on screen. */
export interface SqRowError {
  line: number;
  message: string;
}

/** ApiError.data carried by ROWS_INVALID / NO_VALID_ACCOUNTS.
 *  NO_VALID_ACCOUNTS can legitimately carry an EMPTY rowErrors list. */
export interface SqRowErrorsData {
  rowErrors: SqRowError[];
}

/** ApiError.data carried by ACTIVE_IN_OTHER_RUN: the mailboxes another live run
 *  is already connecting. Nothing was saved. */
export interface SqBusyEmailsData {
  emails: string[];
}

/** ApiError.data carried by NOT_READY on Start: the wizard pieces still missing,
 *  already phrased for display. */
export interface SqMissingData {
  missing: string[];
}

/** POST /:id/start */
export interface SqStartResult {
  runId: string;
  accountsQueued: number;
  /** Null on a connect-only warmuptool run, which resolves no tags. */
  tagId: string | null;
  /** Tag ids the operator had picked that no longer exist in the workspace. They
   *  were dropped rather than assigned - assigning a dead id fails the whole
   *  config push for an account. */
  droppedTagIds: string[];
}

/**
 * Both retry endpoints answer with this shape.
 *
 * `skipped_active` is the half that matters: those mailboxes still had an ACTIVE
 * job, BullMQ would not yank it out from under a running worker, so they were
 * left completely alone - not reset, not re-queued. Reporting a plain "retried"
 * count without them tells the operator a mailbox is being re-driven when it is
 * not. They can be retried again once the in-flight job lands.
 */
export interface SqRetryResult {
  /** Item rows actually reset to `pending`. */
  retried: number;
  /** Jobs actually put back on the queue. Equals retried in the normal case. */
  enqueued: number;
  /** Emails whose job could not be displaced, so nothing was touched for them. */
  skipped_active: string[];
}

/** GET /:id/tags - the workspace's live tag list.
 *
 *  NOTE the field name: the router maps PlusVibe's `_id` to `id` before it
 *  answers, so this is `id`, not `_id`. */
export interface SqTag {
  id: string;
  name: string;
}

/**
 * Every error code this router can return, so callers branch by name instead of
 * matching on message text.
 *
 * BAD_REQUEST           400  malformed body or run id
 * NOT_FOUND             404  no such run, or that mailbox is not in this run
 * RUN_CLOSED            409  archived, or past the editable statuses
 * KEY_INVALID           400  the platform rejected the API key; nothing was
 *                            stored. On Bison and Warmuptool the URL and the key
 *                            are one credential, so it can mean either half is
 *                            wrong
 * ROWS_INVALID          400  bad rows in the paste; data is SqRowErrorsData
 * NO_VALID_ACCOUNTS     400  the paste had no account rows; ditto
 * ACTIVE_IN_OTHER_RUN   409  another live run owns these mailboxes; data is
 *                            SqBusyEmailsData. Nothing was saved.
 * PARTNER_CREDS_INVALID 400  PlusVibe's partner API refused the key for that
 *                            workspace (wrong account, or our provider disabled)
 * WORKSPACE_INVALID     400  workspace not in this key's cached allow-list
 * WRONG_SEQUENCER       400  a sequencer-specific step called on the wrong run
 *                            type - user-id on a PlusVibe run, workspace on a
 *                            Smartlead run, or any of workspace, params and tags
 *                            on a connect-only Warmuptool run
 * PROVIDER_ID_MISSING   500  server has no PLUSVIBE_PROVIDER_ID; nothing started
 * NOT_READY             409  Start before the wizard is complete (data is
 *                            SqMissingData), or a rerun before Start
 * OAUTH_SESSIONS_UNAVAILABLE
 *                       409  Bison and Warmuptool: the instance refused an OAuth
 *                            session mint at Start (on Bison the beta is not
 *                            enabled; on Warmuptool the key lacks WRITE_EMAILS,
 *                            Google connect is off or the workspace is at its
 *                            mailbox allowance). Probed first, so nothing was
 *                            started and nothing was created in the workspace
 * ALREADY_STARTED       409  Start lost the CAS; nothing was queued twice
 * QA_IN_PROGRESS        409  the QA pass re-drives failures itself; do not race it
 * SECRETS_PURGED        409  the borrowed credentials are gone; a new run is needed
 * ENQUEUE_FAILED        500  the run flipped to running and the queue add threw;
 *                            the run is parked `failed` and Retry errors recovers it
 * PLUSVIBE_ERROR        502  upstream call failed; the message is already scrubbed
 * INSTANTLY_ERROR       502  same, from Instantly - a separate code so the UI
 *                            names the right platform
 * BISON_ERROR           502  same, from the customer's Bison instance
 * WARMUPTOOL_ERROR      502  same, from the Warmuptool instance
 *
 * The last four do not come from this router but can still reach a caller, so
 * they are in the union: a `switch` on sqErrorCode() must not get a false
 * exhaustiveness guarantee from tsc and then fall through on a real response.
 *
 * INTERNAL_ERROR        500  an unhandled throw (middleware/error-handler.ts)
 * UNAUTHORIZED          401  session expired (auth middleware, before the router)
 * PARSE_ERROR             -  response body was not JSON (client.ts)
 * UNKNOWN                 -  error body carried no code, OR a code this union
 *                            does not name - see sqErrorCode below
 */
export type SqErrorCode =
  | 'BAD_REQUEST'
  | 'NOT_FOUND'
  | 'RUN_CLOSED'
  | 'KEY_INVALID'
  | 'ROWS_INVALID'
  | 'NO_VALID_ACCOUNTS'
  | 'ACTIVE_IN_OTHER_RUN'
  | 'PARTNER_CREDS_INVALID'
  | 'WORKSPACE_INVALID'
  | 'WRONG_SEQUENCER'
  | 'PROVIDER_ID_MISSING'
  | 'NOT_READY'
  | 'OAUTH_SESSIONS_UNAVAILABLE'
  | 'ALREADY_STARTED'
  | 'QA_IN_PROGRESS'
  | 'SECRETS_PURGED'
  | 'ENQUEUE_FAILED'
  | 'PLUSVIBE_ERROR'
  | 'INSTANTLY_ERROR'
  | 'BISON_ERROR'
  | 'WARMUPTOOL_ERROR'
  | 'INTERNAL_ERROR'
  | 'UNAUTHORIZED'
  | 'PARSE_ERROR'
  | 'UNKNOWN';

/** Runtime twin of SqErrorCode. Kept beside the union so adding a member to one
 *  and not the other is visible in a single screenful. */
const SQ_ERROR_CODES: ReadonlySet<string> = new Set<SqErrorCode>([
  'BAD_REQUEST',
  'NOT_FOUND',
  'RUN_CLOSED',
  'KEY_INVALID',
  'ROWS_INVALID',
  'NO_VALID_ACCOUNTS',
  'ACTIVE_IN_OTHER_RUN',
  'PARTNER_CREDS_INVALID',
  'WORKSPACE_INVALID',
  'WRONG_SEQUENCER',
  'PROVIDER_ID_MISSING',
  'NOT_READY',
  'OAUTH_SESSIONS_UNAVAILABLE',
  'ALREADY_STARTED',
  'QA_IN_PROGRESS',
  'SECRETS_PURGED',
  'ENQUEUE_FAILED',
  'PLUSVIBE_ERROR',
  'INSTANTLY_ERROR',
  'BISON_ERROR',
  'WARMUPTOOL_ERROR',
  'INTERNAL_ERROR',
  'UNAUTHORIZED',
  'PARSE_ERROR',
  'UNKNOWN',
]);

/* ─── Transport ─────────────────────────────────────────────────────────── */

/**
 * Thin wrapper over the shared client so every call in this module goes through
 * one place. `api` unwraps `{ success: true, data }` and throws `ApiError` with
 * `.code`, `.status` and `.data` on `{ success: false, error }` - the codes above
 * are the whole point of this feature's error handling, so the shared helper is
 * used rather than a local one that would flatten them to a message.
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  return api<T>(`${BASE}${path}`, init);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

const enc = encodeURIComponent;

/**
 * The error code of a failed sqApi call, or null when the failure was not an
 * ApiError (a network drop, say).
 *
 * The membership test is the point. `err.code` is a bare string off the wire, so
 * an unchecked cast would hand a caller a value the union does not contain while
 * tsc still believed the union was exhaustive - exactly the false guarantee the
 * union's own comment says it exists to prevent. An unrecognised code is narrowed
 * to 'UNKNOWN', which every caller already has to handle.
 */
export function sqErrorCode(err: unknown): SqErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  return SQ_ERROR_CODES.has(err.code) ? (err.code as SqErrorCode) : 'UNKNOWN';
}

/** Operator-facing text for any thrown value. Server messages are already
 *  scrubbed of every borrowed credential before they leave the router, so they
 *  are safe to render verbatim. */
export function sqErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

/* ─── Endpoints ─────────────────────────────────────────────────────────── */

export const sqApi = {
  /** GET / - 100 most recent runs, newest first, archived ones included. The
   *  caller collapses those; the server does not filter. */
  listRuns: (): Promise<{ runs: SqRun[] }> => req<{ runs: SqRun[] }>(''),

  /** POST / - name is 1..300 chars after trim (the full Google Sheet name).
   *  Returns the new draft run, already stamped with its PI_YYYY_MM_DD tag. */
  createRun: (name: string): Promise<SqRun> => post<SqRun>('', { name }),

  /** GET /:id - the run plus its items, ordered by position. */
  getRun: (runId: string): Promise<SqRunDetail> => req<SqRunDetail>(`/${enc(runId)}`),

  /**
   * POST /:id/sequencer - records the choice and advances the pointer to step 2.
   * A CHANGED platform resets steps 2-5 server-side (key, cache, workspace,
   * accounts, params - they are all platform-specific); `cleared` says whether
   * anything was actually lost, so the caller can warn.
   * Errors: RUN_CLOSED, BAD_REQUEST, NOT_FOUND.
   */
  setSequencer: (runId: string, sequencer: SqSequencer): Promise<{ cleared: boolean }> =>
    post<{ cleared: boolean }>(`/${enc(runId)}/sequencer`, { sequencer }),

  /**
   * POST /:id/user-id - Smartlead only. Confirms the auto-resolved workspace user
   * id or sets the manually entered one, then advances to step 5. The id is a
   * plain identifier, not a secret.
   * Errors: RUN_CLOSED, WRONG_SEQUENCER, BAD_REQUEST, NOT_FOUND.
   */
  setUserId: async (runId: string, userId: string): Promise<void> => {
    await post<unknown>(`/${enc(runId)}/user-id`, { user_id: userId });
  },

  /**
   * POST /:id/api-key - verifies the key against the platform and stores it
   * only if it passes. The key never comes back in any later response, so the
   * caller must drop its own copy the moment this resolves.
   *
   * `baseUrl` is Bison's instance origin, and required there: that platform is
   * white-labeled per customer, so the URL and the key are one credential and
   * are verified as a pair. KEY_INVALID therefore covers both halves on a
   * bison run. Warmuptool is the same pair. It is sent ONLY when passed - the
   * other platforms have a central API host and no origin to send.
   * Errors: RUN_CLOSED, KEY_INVALID, PLUSVIBE_ERROR, INSTANTLY_ERROR,
   * BISON_ERROR, WARMUPTOOL_ERROR, BAD_REQUEST, NOT_FOUND.
   */
  setApiKey: (
    runId: string, apiKey: string, baseUrl?: string,
    options: { resolveSmartleadUserId?: boolean } = {},
  ): Promise<SqSetApiKeyResult> =>
    post<SqSetApiKeyResult>(`/${enc(runId)}/api-key`, {
      api_key: apiKey,
      ...(baseUrl === undefined ? {} : { base_url: baseUrl }),
      ...(options.resolveSmartleadUserId ? { resolve_smartlead_user_id: true } : {}),
    }),

  /**
   * POST /:id/accounts - replaces the whole account list. Re-pasting DELETES
   * every existing item row, which is why the server refuses it once the run
   * has left the wizard. All-or-nothing: a rejected paste stores nothing.
   * Errors: RUN_CLOSED, SECRETS_PURGED, ROWS_INVALID /
   * NO_VALID_ACCOUNTS (data is SqRowErrorsData), ACTIVE_IN_OTHER_RUN (data is
   * SqBusyEmailsData), BAD_REQUEST, NOT_FOUND.
   */
  setAccounts: (runId: string, accountsText: string): Promise<SqSetAccountsResult> =>
    post<SqSetAccountsResult>(`/${enc(runId)}/accounts`, { accounts_text: accountsText }),

  /**
   * POST /:id/workspace - the id must be one of `workspaces_cache`, and on
   * PlusVibe the key is proved against the partner listing before it is stored.
   * Errors: RUN_CLOSED, WORKSPACE_INVALID, SECRETS_PURGED, NOT_READY,
   * PARTNER_CREDS_INVALID, PLUSVIBE_ERROR, BAD_REQUEST, NOT_FOUND.
   */
  setWorkspace: async (runId: string, workspaceId: string): Promise<void> => {
    await post<unknown>(`/${enc(runId)}/workspace`, { workspace_id: workspaceId });
  },

  /**
   * POST /:id/params - the whole profile, every field required. `extra_tags`
   * carries only what the operator picked or typed; `resolved_ids` is rejected
   * here and written by Start alone.
   * Errors: RUN_CLOSED, BAD_REQUEST, NOT_FOUND.
   */
  setParams: async (runId: string, params: SqParams): Promise<void> => {
    await post<unknown>(`/${enc(runId)}/params`, { params });
  },

  /** POST /:id/step - wizard nav pointer, 1..6.
   *  Errors: RUN_CLOSED, BAD_REQUEST, NOT_FOUND. */
  setStep: async (runId: string, step: number): Promise<void> => {
    await post<unknown>(`/${enc(runId)}/step`, { current_step: step });
  },

  /**
   * GET /:id/tags - live tag listing for the run's workspace (or the first
   * cached one before the workspace step). A read-only proxy: it never creates a
   * tag, so browsing the list leaves nothing behind in the customer's workspace.
   * Errors: SECRETS_PURGED, NOT_READY, PLUSVIBE_ERROR, NOT_FOUND.
   */
  listTags: (runId: string): Promise<{ tags: SqTag[] }> =>
    req<{ tags: SqTag[] }>(`/${enc(runId)}/tags`),

  /**
   * POST /:id/start - resolves the tags (find-or-create, so a lost double-click
   * costs a listing and not a duplicate tag), flips the run to `running` under a
   * CAS, then queues one job per mailbox.
   * On a bison run the OAuth session mint is probed BEFORE the tags are
   * resolved, so OAUTH_SESSIONS_UNAVAILABLE means nothing was started and
   * nothing was created in the customer's workspace. A warmuptool run probes the
   * same way and resolves no tags.
   * Errors: RUN_CLOSED, ALREADY_STARTED, SECRETS_PURGED, NOT_READY (data is
   * SqMissingData), PROVIDER_ID_MISSING, OAUTH_SESSIONS_UNAVAILABLE,
   * PLUSVIBE_ERROR, INSTANTLY_ERROR, BISON_ERROR, WARMUPTOOL_ERROR, ENQUEUE_FAILED.
   */
  start: (runId: string): Promise<SqStartResult> => post<SqStartResult>(`/${enc(runId)}/start`),

  /**
   * POST /:id/retry-errors - re-drives every errored mailbox (and, on a run
   * parked `failed` by ENQUEUE_FAILED, the stranded `pending` ones too).
   * Check `skipped_active` before reporting success.
   * Errors: RUN_CLOSED, QA_IN_PROGRESS, SECRETS_PURGED, NOT_FOUND.
   */
  retryErrors: (runId: string): Promise<SqRetryResult> =>
    post<SqRetryResult>(`/${enc(runId)}/retry-errors`),

  /**
   * POST /:id/items/:email/retry - one mailbox, any terminal status. Same
   * `skipped_active` caveat as retryErrors.
   * Errors: RUN_CLOSED, QA_IN_PROGRESS, NOT_READY (run never started),
   * SECRETS_PURGED, BAD_REQUEST, NOT_FOUND (mailbox not in this run).
   */
  retryItem: (runId: string, email: string): Promise<SqRetryResult> =>
    post<SqRetryResult>(`/${enc(runId)}/items/${enc(email)}/retry`),

  /**
   * POST /:id/archive - purges all three borrowed secrets and closes the run.
   * Allowed in EVERY status, deliberately: it is the escape hatch for a run that
   * died mid-flight, whose items would otherwise block those mailboxes from
   * every future run. Every item row survives as the record of what happened.
   * Errors: BAD_REQUEST, NOT_FOUND.
   */
  archive: async (runId: string): Promise<void> => {
    await post<unknown>(`/${enc(runId)}/archive`);
  },
};
